// API Service Layer
import { env } from "../config/env";
import type {
  PayInsCreateRequest,
  PayInsCreateResponse,
  PayInsStatusResponse,
} from "../types/payins";

export class ApiError extends Error {
  public code?: string;
  public status?: number;
  public field?: string;
  public error?: string;
  public details?: Record<string, unknown>;

  constructor(
    message: string,
    code?: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.details = details;
    this.error = message; // Add error property for compatibility
  }
}

export class ApiService {
  private baseURL: string;
  private token: string | null = null;

  constructor() {
    this.baseURL = env.API_BASE_URL;
    this.token = localStorage.getItem("access_token");
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    // Get token dynamically from localStorage
    const token = localStorage.getItem("access_token");

    const config: RequestInit = {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Handle 401 specifically for session expiration
        if (response.status === 401) {
          // Clear tokens on 401
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");

          // Dispatch logout event
          window.dispatchEvent(
            new CustomEvent("auth:logout", {
              detail: { reason: "session_expired" },
            }),
          );

          const sessionError = new ApiError(
            "Sesión expirada, vuelve a iniciar sesión",
            "SESSION_EXPIRED",
            errorData.details,
          );
          sessionError.status = 401;
          sessionError.field = errorData.field;
          throw sessionError;
        }

        const apiError = new ApiError(
          errorData.error ||
            errorData.message ||
            `HTTP ${response.status}: ${response.statusText}`,
          errorData.code || response.status.toString(),
          errorData.details,
        );

        // Add additional properties for error handler compatibility
        apiError.status = response.status;
        apiError.field = errorData.field;

        throw apiError;
      }

      return await response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      // Network or other errors
      throw new ApiError(
        "Error de conexión. Verifica tu conexión a internet.",
        "NETWORK_ERROR",
        error instanceof Error
          ? { message: error.message, stack: error.stack }
          : { error: String(error) },
      );
    }
  }

  // Generic HTTP methods
  async post<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: "GET",
    });
  }

  async put<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: "DELETE",
    });
  }

  // Auth endpoints
  async login(email: string, password: string) {
    const response = await this.request<{
      success: boolean;
      token: string;
      refreshToken: string;
      expiresAt: string;
      user: {
        email: string;
        firstName: string;
        lastName: string;
        roles: string[];
      };
    }>("/api/v1/Auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });

    if (response.success) {
      this.token = response.token;
      localStorage.setItem("access_token", response.token);
      localStorage.setItem("refresh_token", response.refreshToken);
    }

    return response;
  }

  async register(userData: Record<string, unknown>) {
    const response = await this.request<{
      success: boolean;
      message: string;
      token: string;
      refreshToken: string;
      expiresAt: string;
      user: {
        email: string;
        firstName: string;
        lastName: string;
        roles: string[];
      };
    }>("/api/v1/Auth/register", {
      method: "POST",
      body: JSON.stringify(userData),
    });

    if (response.success) {
      this.token = response.token;
      localStorage.setItem("access_token", response.token);
      localStorage.setItem("refresh_token", response.refreshToken);
    }

    return response;
  }

  async changePassword(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
  ) {
    return this.request<{
      message: string;
    }>("/api/v1/Auth/change-password", {
      method: "POST",
      body: JSON.stringify({
        currentPassword,
        newPassword,
        confirmPassword,
      }),
    });
  }

  async logout() {
    // Clear tokens immediately (before API call)
    // This ensures logout works even if token is expired
    const refreshToken = localStorage.getItem("refresh_token");
    this.token = null;

    try {
      // Try to call logout API to revoke refresh token on server
      // But don't fail if token is expired or invalid
      const token = localStorage.getItem("access_token");
      const url = `${this.baseURL}/api/v1/auth/logout`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          refreshToken: refreshToken || null,
        }),
      });

      // Only log warning if it's not a 401 (token expired/invalid)
      // 401 is expected if token is already expired
      if (!response.ok && response.status !== 401) {
        const errorData = await response.json().catch(() => ({}));
        console.warn("Logout API call failed:", errorData);
      }
    } catch (error) {
      // Continue with logout even if API call fails
      // This is expected if token is expired
      console.warn(
        "Logout API call failed (this is OK if token was expired):",
        error,
      );
    } finally {
      // Always clear tokens locally, regardless of API call result
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
    }
  }

  async refreshToken() {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      throw new ApiError("No refresh token available", "NO_REFRESH_TOKEN");
    }

    const response = await this.request<{
      success: boolean;
      token: string;
      refreshToken: string;
      expiresAt: string;
      user: {
        email: string;
        firstName: string;
        lastName: string;
        roles: string[];
      };
    }>("/api/v1/Auth/refresh", {
      method: "POST",
      body: JSON.stringify({ refreshToken }),
    });

    if (response.success) {
      this.token = response.token;
      localStorage.setItem("access_token", response.token);
      localStorage.setItem("refresh_token", response.refreshToken);
    }

    return response.token;
  }

  async getAuthProfile() {
    return this.request<{
      email: string;
      firstName: string;
      lastName: string;
      roles: string[];
    }>("/api/v1/Auth/me");
  }

  async googleLogin(idToken: string) {
    const response = await this.request<{
      success: boolean;
      token: string;
      refreshToken: string;
      expiresAt: string;
      user: {
        email: string;
        firstName: string;
        lastName: string;
        roles: string[];
      };
    }>("/api/v1/Auth/google/token", {
      method: "POST",
      body: JSON.stringify({ idToken }),
    });

    if (response.success) {
      this.token = response.token;
      localStorage.setItem("access_token", response.token);
      if (response.refreshToken) {
        localStorage.setItem("refresh_token", response.refreshToken);
      }
    }

    return response;
  }

  // Payments endpoints
  async getPayments(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== "") {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/api/v1/payments/pending${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    return this.request<{
      success: boolean;
      data: Record<string, unknown>[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>(endpoint);
  }

  async getPayment(id: string) {
    return this.request<{
      success: boolean;
      data: Record<string, unknown>;
    }>(`/payments/${id}`);
  }

  async createPayment(paymentData: Record<string, unknown>) {
    return this.request<{
      success: boolean;
      data: Record<string, unknown>;
    }>("/payments", {
      method: "POST",
      body: JSON.stringify(paymentData),
    });
  }

  async updatePayment(id: string, updateData: Record<string, unknown>) {
    return this.request<{
      success: boolean;
      data: Record<string, unknown>;
    }>(`/payments/${id}`, {
      method: "PUT",
      body: JSON.stringify(updateData),
    });
  }

  // Users endpoints
  async getUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== "") {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/api/v1/users${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    return this.request<{
      success: boolean;
      data: Record<string, unknown>[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>(endpoint);
  }

  async createUser(userData: Record<string, unknown>) {
    return this.request<{
      success: boolean;
      data: Record<string, unknown>;
    }>("/users", {
      method: "POST",
      body: JSON.stringify(userData),
    });
  }

  async updateUser(id: string, userData: Record<string, unknown>) {
    return this.request<{
      success: boolean;
      data: Record<string, unknown>;
    }>(`/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(userData),
    });
  }

  // Bank accounts endpoints
  async getBankAccounts() {
    return this.request<{
      success: boolean;
      data: Record<string, unknown>[];
    }>("/api/v1/BankAccounts");
  }

  async addBankAccount(accountData: Record<string, unknown>) {
    return this.request<{
      success: boolean;
      data: Record<string, unknown>;
    }>("/api/v1/BankAccounts", {
      method: "POST",
      body: JSON.stringify(accountData),
    });
  }

  async setPrimaryBankAccount(id: string) {
    return this.request<{
      success: boolean;
      data: Record<string, unknown>;
    }>(`/api/v1/BankAccounts/${id}/primary`, {
      method: "PUT",
    });
  }

  async deleteBankAccount(id: string) {
    return this.request<{
      success: boolean;
      message?: string;
    }>(`/api/v1/BankAccounts/${id}`, {
      method: "DELETE",
    });
  }

  // Dashboard endpoints
  async getDashboardStats() {
    return this.request<{
      success: boolean;
      data: {
        totalTransactions: number;
        pendingTransactions: number;
        completedTransactions: number;
        totalVolume: number;
        totalVolumeUSD: number;
        totalVolumePEN: number;
        averageExchangeRate: number;
        monthlyGrowth: number;
      };
    }>("/dashboard/stats");
  }

  async getAdminStats() {
    return this.request<{
      success: boolean;
      data: {
        totalRequests: number;
        pendingRequests: number;
        completedRequests: number;
        totalVolumePEN: number;
        monthlyGrowth: number;
        totalClients: number;
        activeClients: number;
      };
    }>("/api/v1/dashboard/admin-stats");
  }

  async getClientStats() {
    return this.request<{
      success: boolean;
      data: {
        totalRequests: number;
        pendingRequests: number;
        completedRequests: number;
        totalVolumePEN: number;
        monthlyGrowth: number;
      };
    }>("/api/v1/dashboard/client-stats");
  }

  // Cash-out endpoints
  async getProfile() {
    return this.request<{
      success: boolean;
      data: Record<string, unknown>; // The backend returns { success: true, data: profile }
    }>("/api/v1/Profile");
  }

  async createProfile(profileData: Record<string, unknown>) {
    return this.request<{
      success: boolean;
      data: {
        profile: Record<string, unknown>;
      };
    }>("/api/v1/Profile", {
      method: "POST",
      body: JSON.stringify(profileData),
    });
  }

  async updateProfile(profileData: Record<string, unknown>) {
    return this.request<{
      success: boolean;
      data: {
        profile: Record<string, unknown>;
      };
    }>("/api/v1/Profile", {
      method: "PUT",
      body: JSON.stringify(profileData),
    });
  }

  async createCashoutRequest(requestData: { amountPEN: number }) {
    return this.request<{
      success: boolean;
      data: {
        id: string;
        reference: string;
        paymentUrl?: string;
        amountPEN: number;
        commissionPEN: number;
        netPEN: number;
      };
    }>("/api/v1/Requests", {
      method: "POST",
      body: JSON.stringify(requestData),
    });
  }

  /**
   * Pre-validación antes de iniciar el flujo de pago.
   * Devuelve si la solicitud es viable: perfil completo, monto válido y al menos un
   * proveedor con saldo. El frontend debe invocarlo antes de mostrar el botón "Pagar".
   */
  async preflightCashoutRequest(amountPEN: number) {
    return this.post<{
      success: boolean;
      canProceed: boolean;
      code: string;
      message: string;
      providerHint?: string | null;
      degradedMode: boolean;
      providerStates: Array<{
        provider: string;
        querySucceeded: boolean;
        availableAmount: number;
        sufficient: boolean;
        errorMessage?: string | null;
      }>;
      netPEN: number;
      commissionPEN: number;
    }>("/api/v1/Requests/preflight", { amountPEN });
  }

  async getCashoutRequest(id: string) {
    return this.request<{
      success: boolean;
      data: {
        id: string;
        userId: string;
        amountPEN: number;
        commissionPEN: number;
        netPEN: number;
        status: string;
        reference: string;
        paymentUrl?: string;
        createdAt: string;
        updatedAt: string;
        paidAt?: string;
        confirmedAt?: string;
        payoutStartedAt?: string;
        payoutCompletedAt?: string;
        payoutFailedAt?: string;
        errorMessage?: string;
        payoutProvider?: string | null;
        payoutProviderReference?: string | null;
        payoutSafetyPayStatusCode?: string | null;
        payoutDecisionReason?: string | null;
        payoutErrorMessage?: string | null;
        user?: Record<string, unknown>;
      };
    }>(`/api/v1/Requests/${id}`);
  }

  /**
   * Timeline unificado (CashoutRequest + PayIn + PayOut) para la pestaña "Detalle técnico".
   */
  async getCashoutRequestTimeline(id: string) {
    return this.get<{
      success: boolean;
      cashoutReference: string;
      payInReference?: string | null;
      payOutReference?: string | null;
      entries: Array<{
        id: string;
        entityType: string;
        reference: string;
        previousStatus: string;
        newStatus: string;
        changeSource: string;
        reason?: string | null;
        errorMessage?: string | null;
        createdAt: string;
        additionalData?: string | null;
      }>;
    }>(`/api/v1/Requests/${id}/timeline`);
  }

  /**
   * @deprecated Usar adminPayOutsService.getProviderBalance()
   */
  async getPayoutBalance(provider = "Prefondeo", currency = "PEN", take = 50) {
    const { adminPayOutsService } = await import("./adminPayOutsService");
    return adminPayOutsService.getProviderBalance(provider, currency, take);
  }

  /**
   * @deprecated Usar adminPayOutsService.registerProviderCredit()
   */
  async registerPayoutBalanceCredit(payload: {
    provider?: string;
    currency?: string;
    amount: number;
    notes?: string;
  }) {
    const { adminPayOutsService } = await import("./adminPayOutsService");
    return adminPayOutsService.registerProviderCredit({
      provider: payload.provider ?? "Prefondeo",
      currency: payload.currency ?? "PEN",
      amount: payload.amount,
      notes: payload.notes,
    });
  }

  // ---------------------------------------------------------------------------
  // Admin: Alerts (Fase 1 alerting)
  // ---------------------------------------------------------------------------

  /**
   * (Admin) Lista paginada de alertas con filtros opcionales.
   */
  async listAlerts(params?: {
    status?: string;
    severity?: string;
    provider?: string;
    from?: string;
    to?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  }) {
    const qs = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") {
          qs.append(k, String(v));
        }
      });
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return this.get<import("../types/alerts").PayoutAlertListResponse>(
      `/api/v1/admin/alerts${suffix}`,
    );
  }

  /** (Admin) Detalle de una alerta. */
  async getAlertDetail(id: string) {
    return this.get<{
      success: boolean;
      alert: import("../types/alerts").PayoutAlertDetail;
    }>(`/api/v1/admin/alerts/${id}`);
  }

  /** (Admin) Marcar una alerta como acknowledgeada. */
  async acknowledgeAlert(id: string, notes?: string) {
    return this.post<{
      success: boolean;
      alert: import("../types/alerts").PayoutAlertDetail;
    }>(`/api/v1/admin/alerts/${id}/ack`, { notes });
  }

  /** (Admin) Cerrar manualmente una alerta. */
  async resolveAlert(id: string, reason?: string) {
    return this.post<{
      success: boolean;
      alert: import("../types/alerts").PayoutAlertDetail;
    }>(`/api/v1/admin/alerts/${id}/resolve`, { reason });
  }

  /** (Admin) Counters por severidad/status para los últimos N días. */
  async getAlertStats(days = 7) {
    return this.get<import("../types/alerts").PayoutAlertStats>(
      `/api/v1/admin/alerts/stats?days=${days}`,
    );
  }

  /** (Admin) Lista silences (por default sólo activos). */
  async listAlertSilences(includeExpired = false) {
    return this.get<{
      success: boolean;
      items: import("../types/alerts").PayoutAlertSilence[];
    }>(
      `/api/v1/admin/alerts/silences?includeExpired=${includeExpired ? "true" : "false"}`,
    );
  }

  /** (Admin) Crea un silence sobre una dedupKey hasta una fecha futura. */
  async createAlertSilence(payload: {
    dedupKey: string;
    until: string; // ISO 8601
    reason?: string;
  }) {
    return this.post<{ success: boolean; id: string }>(
      `/api/v1/admin/alerts/silences`,
      payload,
    );
  }

  /** (Admin) Revoca (cancela) un silence antes de su vencimiento. */
  async revokeAlertSilence(id: string) {
    return this.delete<{ success: boolean }>(
      `/api/v1/admin/alerts/silences/${id}`,
    );
  }

  async getCashoutRequests(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== "") {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/api/v1/Requests${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    return this.request<{
      success: boolean;
      data: Record<string, unknown>[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>(endpoint);
  }

  // Manual payout endpoint (SimpleApi format)
  async createManualPayout(payoutData: Record<string, unknown>) {
    return this.request<{
      success: boolean;
      id?: string;
      reference?: string;
      status?: string;
      errorMessage?: string;
      rawResponse?: string;
    }>("/api/v1/Payments", {
      method: "POST",
      body: JSON.stringify(payoutData),
    });
  }

  // PayOuts endpoint (Payments.Api format)
  async createPayout(payoutData: Record<string, unknown>) {
    return this.request<{
      success: boolean;
      id?: string;
      reference?: string;
      status?: string;
      errorMessage?: string;
      rawResponse?: string;
    }>("/api/v1/PayOuts", {
      method: "POST",
      body: JSON.stringify(payoutData),
    });
  }

  // Legacy exchange rates endpoint (deprecated)
  async getExchangeRates() {
    return this.request<{
      success: boolean;
      data: {
        buy: number;
        sell: number;
        previousBuy: number;
        previousSell: number;
        lastUpdated: string;
      };
    }>("/exchange-rates");
  }

  // My transactions (client-specific)
  async getMyTransactions(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== "") {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/api/v1/dashboard/my-transactions${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    return this.request<{
      success: boolean;
      data: Record<string, unknown>[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>(endpoint);
  }

  // ==========================================
  // PayIns Endpoints
  // ==========================================

  /**
   * Creates a new PayIn transaction
   * POST /api/payins
   */
  async createPayIn(
    request: PayInsCreateRequest,
  ): Promise<PayInsCreateResponse> {
    return this.post<PayInsCreateResponse>("/api/v1/PayIns", request);
  }

  /**
   * Gets the status of a PayIn transaction
   * GET /api/v1/PayIns/{reference}
   */
  async getPayInStatus(reference: string): Promise<PayInsStatusResponse> {
    return this.get<PayInsStatusResponse>(`/api/v1/PayIns/${reference}`);
  }

  /**
   * Gets the PayIns configuration (for debugging)
   * GET /api/v1/PayIns/config
   */
  async getPayInsConfig() {
    return this.get<{
      baseUrl: string;
      apiKeyName: string;
      apiKey: string;
      environment: string;
    }>("/api/v1/PayIns/config");
  }
}

// Export singleton instance
export const apiService = new ApiService();
